/**
 * RCC Surface #6 — Death Notice / Deceased Customer workflow.
 *
 * Wave 2 rebuild (locked 2026-04-27 "go all defaults" — see
 * project_death_notice_decisions_20260427.md). Replaces the prior 186-line
 * toy form with a 4-step wizard wired to /v3/death-notice/cases:
 *
 *   1. Intake          — CIF, decedent, DoD, responsible FSR, processor
 *   2. Path            — joint-survivor / individual-pod / individual-no-pod
 *                        / sdb-all-deceased / healthnet
 *   3. Documents       — checklist gated on path (death cert always; estate
 *                        docs only when no-POD; HealthNet release only on
 *                        healthnet path; etc.)
 *   4. Cadence schedule — preview the 30/45/60 cadence with letters; press
 *                        Submit to commit the case + spawn FollowUpTasks
 *
 * Pilot-gated end-to-end. When the officer is NOT enrolled in
 * `pilot.deceasedWorkflow`, the intake API returns `pilotGated:true` and
 * the wizard renders the legacy-fallback notice (with a "Use legacy
 * procedure" hand-off button).
 */

import { useMemo, useState } from 'react';
import { useApiClient } from '@/contexts/ApiClientContext';
import { useAuth } from '@/contexts/AuthContext';
import { useConfig } from '@/contexts/ConfigContext';
import { Button, Pill } from '@/components/primitives';
import { SurfaceHeader } from '../components/SurfaceHeader';
import type {
  DeathNoticePath,
  DeathNoticeIntakeResult,
} from '@retail-ops/shared-types';

type WizardStep = 'intake' | 'path' | 'documents' | 'cadence' | 'submitted';

interface IntakeFields {
  cifId: string;
  decedentName: string;
  dateOfDeath: string;
  responsibleFsrUpn: string;
  fsrProcessorUpn: string;
}

const PATH_LABELS: Record<DeathNoticePath, string> = {
  'joint-survivor':    'Joint account — survivor remains',
  'individual-pod':    'Individual account — POD beneficiary payout',
  'individual-no-pod': 'Individual account — Estate claim (no POD)',
  'sdb-all-deceased':  'SDB — All owners deceased',
  'healthnet':         'HealthNet (Medicaid) decedent',
};

const PATH_BLURBS: Record<DeathNoticePath, string> = {
  'joint-survivor':    'Cadence runs 30/45/60. Day-60 forces sig-card REPLACE if no titling change.',
  'individual-pod':    'Verify BP/BC codes; release POD payout to beneficiary on death cert receipt.',
  'individual-no-pod': 'Estate-claim path. R&HH ≤ $1k; full Estate documentation > $1k. MO 1-yr-30-day hold applies.',
  'sdb-all-deceased':  'Hand off to RSM SDB All-Deceased wizard for joint family inventory.',
  'healthnet':         'Coordinate with Kent + MO DSS. 13-month state-claim hold per RSMo 473.398.6.',
};

interface DocChecklistItem {
  code: string;
  label: string;
  required: boolean;
  helper?: string;
}

function docsForPath(path: DeathNoticePath): DocChecklistItem[] {
  const death = { code: 'DEATH-CERT', label: 'Certified death certificate', required: true };
  switch (path) {
    case 'joint-survivor':
      return [
        death,
        { code: 'NEW-SIG-CARD', label: 'New Deposit Pro signature card (survivor primary)', required: true },
        { code: 'NEW-W9',       label: 'New W-9 confirmation (survivor)',                    required: true },
      ];
    case 'individual-pod':
      return [
        death,
        { code: 'BENEF-ID',     label: 'Beneficiary government ID + proof of address',       required: true },
        { code: 'BENEF-W9',     label: 'Beneficiary W-9 (if cash payout > $600)',            required: false },
      ];
    case 'individual-no-pod':
      return [
        death,
        { code: 'LETTERS-TEST', label: 'Letters Testamentary / Letters of Administration',    required: true,  helper: 'County probate court issues. Personal Rep authority.' },
        { code: 'EIN',          label: 'Federal EIN for the Estate',                          required: true },
        { code: 'PR-ID',        label: 'Personal Rep government photo ID + Address Verification', required: true },
        { code: 'CDD-ESTATE',   label: 'Estate CDD (treated as business per Field 39 = N)',  required: true },
        { code: 'CIP-ALL',      label: 'CIP for all parties on the Estate account',           required: true },
        { code: 'COURT-ASSIGN', label: 'Court-Order Assignment (when assigned to PR)',        required: false },
        { code: 'DN-CLAIM',     label: 'Claim Against Estate v20.0 (balance > $1,000)',       required: false },
        { code: 'DN-RHH',       label: 'Release and Hold Harmless v18.5 (balance ≤ $1,000)',  required: false, helper: 'PCB internal threshold; supersedes full estate claim under $1k.' },
      ];
    case 'sdb-all-deceased':
      return [
        death,
        { code: 'SDB-INVENTORY', label: 'Joint inventory list (officer + family member)',     required: true,  helper: 'Officer must enter box WITH family member.' },
        { code: 'WILL-COPY',     label: 'Copy of will (if found in box; original returned to family)', required: false },
      ];
    case 'healthnet':
      return [
        death,
        { code: 'HN-RELEASE',   label: 'HealthNet Release form',                              required: true,  helper: 'Required before estate disbursement. Kent coordinates with MO DSS.' },
        { code: 'SSA-1',        label: 'SSA letter sample 1 (Rep Payee)',                     required: false, helper: 'Required when decedent was a Rep Payee.' },
        { code: 'SSA-2',        label: 'SSA letter sample 2',                                  required: false },
        { code: 'SSA-3',        label: 'SSA letter sample 3',                                  required: false },
        { code: 'STATE-CLAIM',  label: 'State Claim letter (MO HealthNet participant)',       required: false },
      ];
  }
}

export function DeathNotice(): JSX.Element {
  const apiClient = useApiClient();
  const { user } = useAuth();
  const { config } = useConfig();
  const [step, setStep] = useState<WizardStep>('intake');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [intakeResult, setIntakeResult] = useState<DeathNoticeIntakeResult | null>(null);

  const todayIso = useMemo(() => new Date().toISOString().slice(0, 10), []);
  const moHoldMode = (config?.systemSettings?.['deceased.moHoldMode'] as string | undefined) ?? 'warn';
  const officerCode = user?.officerCode ?? '';
  const userUpn = user?.upn ?? '';

  const [fields, setFields] = useState<IntakeFields>({
    cifId: '',
    decedentName: '',
    dateOfDeath: todayIso,
    responsibleFsrUpn: userUpn,
    fsrProcessorUpn: '',
  });
  const [path, setPath] = useState<DeathNoticePath | null>(null);
  const [checked, setChecked] = useState<Record<string, boolean>>({});

  const docs = path ? docsForPath(path) : [];
  const requiredDocs = docs.filter((d) => d.required);
  const allRequiredChecked = requiredDocs.every((d) => checked[d.code]);

  const moHoldEndsAt = useMemo(() => {
    if (!fields.dateOfDeath) return null;
    const d = new Date(fields.dateOfDeath);
    if (Number.isNaN(d.getTime())) return null;
    d.setDate(d.getDate() + 395);
    return d.toISOString().slice(0, 10);
  }, [fields.dateOfDeath]);

  const cadencePreview = useMemo(() => {
    if (!fields.dateOfDeath) return [];
    const d0 = new Date(fields.dateOfDeath);
    if (Number.isNaN(d0.getTime())) return [];
    return [30, 45, 60].map((offset) => {
      const due = new Date(d0);
      due.setDate(due.getDate() + offset);
      return {
        offset,
        dueIso: due.toISOString().slice(0, 10),
        letter: offset === 30 ? 'DN-1ST (1st letter)' : offset === 45 ? 'DN-2ND (2nd letter)' : 'Forced REPLACE sig-card',
      };
    });
  }, [fields.dateOfDeath]);

  const validateIntake = (): string | null => {
    if (!fields.cifId.trim()) return 'CIF ID is required.';
    if (!fields.decedentName.trim()) return 'Decedent name is required.';
    if (!fields.dateOfDeath) return 'Date of death is required.';
    if (!fields.responsibleFsrUpn.trim()) return 'Responsible FSR (UPN) is required.';
    return null;
  };

  const submit = async (): Promise<void> => {
    if (!path) {
      setError('Path is required.');
      return;
    }
    setError(null);
    setSubmitting(true);
    try {
      // Bypass key — Wave 1 routes still require it during the auth-hardening
      // soak period. Pulled from VITE_ env or admin context (placeholder
      // empty string falls through to AdminAuth bearer-token path when
      // SystemSettings.admin.auth.allowBypassKey is false).
      const bypassKey = (import.meta.env.VITE_ADMIN_BYPASS_KEY as string | undefined) ?? '';
      const res = await apiClient.retail.deathNotice.intake(bypassKey, {
        cifId: fields.cifId.trim(),
        decedentName: fields.decedentName.trim(),
        dateOfDeath: fields.dateOfDeath,
        path,
        responsibleFsrUpn: fields.responsibleFsrUpn.trim(),
        fsrProcessorUpn: fields.fsrProcessorUpn.trim() || undefined,
        assignedOfficerCode: officerCode || undefined,
      });
      if (!res.success || !res.result) {
        setError(res.error ?? 'Intake failed.');
        return;
      }
      setIntakeResult(res.result);
      setStep('submitted');
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : 'Intake failed.';
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  // ── render ─────────────────────────────────────────────────────────────

  const StepBadge = ({ active, n, label }: { active: boolean; n: number; label: string }) => (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      gap: 'var(--sp-2)',
      padding: 'var(--sp-2) var(--sp-3)',
      borderRadius: 'var(--r-sm)',
      background: active ? 'var(--brand-50, #e6f0fa)' : 'transparent',
      color: active ? 'var(--brand-900, #1a406a)' : 'var(--ink-700)',
      fontWeight: active ? 600 : 400,
      fontSize: 12,
    }}>
      <span style={{
        width: 20, height: 20, borderRadius: 999, background: active ? 'var(--brand-700, #2968ad)' : 'var(--ink-300)',
        color: 'var(--on-brand)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700,
      }}>{n}</span>
      <span>{label}</span>
    </div>
  );

  return (
    <div>
      <SurfaceHeader
        title="Death Notice / Deceased Customer"
        scope="4-step wizard. Pilot-gated; non-pilot officers fall through to legacy procedure."
        surfaceNumber={6}
        role="FSR"
      />

      <div style={{ display: 'flex', gap: 'var(--sp-3)', marginBottom: 'var(--sp-5)', flexWrap: 'wrap' }}>
        <StepBadge active={step === 'intake'}    n={1} label="Intake" />
        <StepBadge active={step === 'path'}      n={2} label="Path" />
        <StepBadge active={step === 'documents'} n={3} label="Documents" />
        <StepBadge active={step === 'cadence'}   n={4} label="Cadence" />
      </div>

      {/* ── Step 1 — Intake ─────────────────────────────────────────────── */}
      {step === 'intake' && (
        <div style={{ display: 'grid', gap: 'var(--sp-4)', maxWidth: 720 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--sp-4)' }}>
            <Field label="CIF ID">
              <input
                type="text"
                value={fields.cifId}
                onChange={(e) => setFields({ ...fields, cifId: e.target.value })}
                style={inputStyle}
              />
            </Field>
            <Field label="Date of death">
              <input
                type="date"
                value={fields.dateOfDeath}
                onChange={(e) => setFields({ ...fields, dateOfDeath: e.target.value })}
                style={inputStyle}
              />
            </Field>
          </div>
          <Field label="Decedent name">
            <input
              type="text"
              value={fields.decedentName}
              onChange={(e) => setFields({ ...fields, decedentName: e.target.value })}
              style={inputStyle}
            />
          </Field>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--sp-4)' }}>
            <Field label="Responsible FSR (UPN)">
              <input
                type="text"
                value={fields.responsibleFsrUpn}
                onChange={(e) => setFields({ ...fields, responsibleFsrUpn: e.target.value })}
                style={inputStyle}
              />
            </Field>
            <Field label="FSR Processor (UPN, optional)">
              <input
                type="text"
                value={fields.fsrProcessorUpn}
                onChange={(e) => setFields({ ...fields, fsrProcessorUpn: e.target.value })}
                style={inputStyle}
              />
            </Field>
          </div>
          {moHoldEndsAt && (
            <div style={infoNoteStyle}>
              MO 1-year + 30-day hold ends <strong>{moHoldEndsAt}</strong> (per RSMo 473.020.2).
              Mode: <Pill variant={moHoldMode === 'block' ? 'err' : 'warn'}>{moHoldMode}</Pill>
            </div>
          )}
          {error && <div style={errorStyle} role="alert">{error}</div>}
          <div style={{ display: 'flex', gap: 'var(--sp-3)' }}>
            <Button variant="primary" onClick={() => {
              const v = validateIntake();
              if (v) { setError(v); return; }
              setError(null);
              setStep('path');
            }}>Next: Path</Button>
          </div>
        </div>
      )}

      {/* ── Step 2 — Path ───────────────────────────────────────────────── */}
      {step === 'path' && (
        <div style={{ display: 'grid', gap: 'var(--sp-3)', maxWidth: 720 }}>
          {(Object.keys(PATH_LABELS) as DeathNoticePath[]).map((p) => (
            <button
              key={p}
              type="button"
              onClick={() => setPath(p)}
              style={{
                textAlign: 'left',
                padding: 'var(--sp-4)',
                borderRadius: 'var(--r-md)',
                border: path === p ? '2px solid var(--brand-700, #2968ad)' : '1px solid var(--ink-300)',
                background: path === p ? 'var(--brand-50, #e6f0fa)' : 'var(--surface-card)',
                cursor: 'pointer',
              }}
            >
              <div style={{ fontWeight: 600, fontSize: 13 }}>{PATH_LABELS[p]}</div>
              <div style={{ fontSize: 12, color: 'var(--ink-700)', marginTop: 'var(--sp-1)' }}>{PATH_BLURBS[p]}</div>
            </button>
          ))}
          <div style={{ display: 'flex', gap: 'var(--sp-3)' }}>
            <Button variant="ghost" onClick={() => setStep('intake')}>Back</Button>
            <Button variant="primary" disabled={!path} onClick={() => setStep('documents')}>Next: Documents</Button>
          </div>
        </div>
      )}

      {/* ── Step 3 — Documents ──────────────────────────────────────────── */}
      {step === 'documents' && path && (
        <div style={{ display: 'grid', gap: 'var(--sp-3)', maxWidth: 720 }}>
          <div style={{ fontSize: 12, color: 'var(--ink-700)' }}>
            Checklist for path: <strong>{PATH_LABELS[path]}</strong>
          </div>
          {docs.map((doc) => (
            <label key={doc.code} style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 'var(--sp-3)',
              padding: 'var(--sp-3)',
              border: '1px solid var(--ink-300)',
              borderRadius: 'var(--r-md)',
              cursor: 'pointer',
            }}>
              <input
                type="checkbox"
                checked={!!checked[doc.code]}
                onChange={(e) => setChecked({ ...checked, [doc.code]: e.target.checked })}
                style={{ marginTop: 2 }}
              />
              <div>
                <div style={{ fontSize: 13, fontWeight: doc.required ? 600 : 400 }}>
                  {doc.label} {doc.required && <span style={{ color: 'var(--err-text, #b10505)' }}>*</span>}
                </div>
                {doc.helper && <div style={{ fontSize: 11, color: 'var(--ink-700)', marginTop: 2 }}>{doc.helper}</div>}
              </div>
            </label>
          ))}
          <div style={{ fontSize: 11, color: 'var(--ink-700)' }}>
            * Required documents must be received before case can close. Optional rows can be checked later.
          </div>
          {path === 'individual-no-pod' && (
            <div style={infoNoteStyle}>
              <strong>Estate workflow cross-link:</strong> when ready, open the RAO Estate wizard with
              decedent + Personal Rep pre-filled. (Wave 3 deep-link to <code>/rao?intent=estate&decedentCif={fields.cifId}</code>)
            </div>
          )}
          {path === 'sdb-all-deceased' && (
            <div style={infoNoteStyle}>
              SDB inventory + escheat handoff is owned by RSM. Use <code>/rsm</code> → Box Lookup → "All-Deceased Wizard".
            </div>
          )}
          {path === 'healthnet' && (
            <div style={infoNoteStyle}>
              HealthNet Release surface is RCC #17 (<code>/rcc/healthnet-release</code>). Kent owns coordination with MO DSS.
            </div>
          )}
          <div style={{ display: 'flex', gap: 'var(--sp-3)' }}>
            <Button variant="ghost" onClick={() => setStep('path')}>Back</Button>
            <Button variant="primary" onClick={() => setStep('cadence')}>Next: Cadence</Button>
          </div>
        </div>
      )}

      {/* ── Step 4 — Cadence preview + commit ────────────────────────────── */}
      {step === 'cadence' && path && (
        <div style={{ display: 'grid', gap: 'var(--sp-4)', maxWidth: 720 }}>
          <div style={{ fontSize: 13 }}>
            On submit, this case spawns 3 FollowUpTasks (one per cadence milestone) routed to the Responsible FSR + FSR Processor distribution.
          </div>
          <div style={{ display: 'grid', gap: 'var(--sp-2)' }}>
            {cadencePreview.map((c) => (
              <div key={c.offset} style={{
                display: 'grid',
                gridTemplateColumns: '60px 120px 1fr',
                gap: 'var(--sp-3)',
                padding: 'var(--sp-3)',
                border: '1px solid var(--ink-300)',
                borderRadius: 'var(--r-md)',
                fontSize: 12,
              }}>
                <strong>Day {c.offset}</strong>
                <span>{c.dueIso}</span>
                <span>{c.letter}</span>
              </div>
            ))}
          </div>
          {!allRequiredChecked && (
            <div style={infoNoteStyle}>
              {requiredDocs.length - requiredDocs.filter((d) => checked[d.code]).length} required document(s) still pending.
              You can still create the case — checklist completion gates only the case-close step.
            </div>
          )}
          {error && <div style={errorStyle} role="alert">{error}</div>}
          <div style={{ display: 'flex', gap: 'var(--sp-3)' }}>
            <Button variant="ghost" onClick={() => setStep('documents')} disabled={submitting}>Back</Button>
            <Button variant="primary" onClick={() => void submit()} disabled={submitting}>
              {submitting ? 'Submitting...' : 'Create Death Notice case'}
            </Button>
          </div>
        </div>
      )}

      {/* ── Confirmation ─────────────────────────────────────────────────── */}
      {step === 'submitted' && intakeResult && (
        <div style={{ display: 'grid', gap: 'var(--sp-4)', maxWidth: 720 }}>
          {intakeResult.pilotGated ? (
            <>
              <div style={infoNoteStyle}>
                <strong>Pilot gate active.</strong> Your officer code is not enrolled in the
                <code> deceasedWorkflow</code> pilot; the ROP case was NOT created.
                Use the legacy procedure (Outlook reminder + word-merge letters) for this customer.
              </div>
              <div>
                <Button variant="ghost" onClick={() => { setStep('intake'); setIntakeResult(null); }}>Start over</Button>
              </div>
            </>
          ) : (
            <>
              <div style={successStyle}>
                <strong>Death Notice case #{intakeResult.caseId} created.</strong>
                {' '}Lifecycle event #{intakeResult.lifecycleEventId} logged.
                {intakeResult.moHoldEndsAt && <> MO hold ends <strong>{intakeResult.moHoldEndsAt}</strong>.</>}
              </div>
              <div style={{ fontSize: 13 }}>3 FollowUpTasks created:</div>
              <div style={{ display: 'grid', gap: 'var(--sp-2)' }}>
                {intakeResult.cadence.map((c) => (
                  <div key={c.cadenceTaskId} style={{
                    display: 'grid',
                    gridTemplateColumns: '60px 120px 1fr 100px',
                    gap: 'var(--sp-3)',
                    padding: 'var(--sp-3)',
                    border: '1px solid var(--ink-300)',
                    borderRadius: 'var(--r-md)',
                    fontSize: 12,
                  }}>
                    <strong>Day {c.dayOffset}</strong>
                    <span>{c.dueAt.slice(0, 10)}</span>
                    <span>{c.letterTemplateCode ?? 'Forced REPLACE sig-card'}</span>
                    <Pill variant={c.status === 'pending' ? 'warn' : 'ok'}>{c.status}</Pill>
                  </div>
                ))}
              </div>
              <div>
                <Button variant="ghost" onClick={() => { setStep('intake'); setIntakeResult(null); setFields({ ...fields, cifId: '', decedentName: '' }); setPath(null); setChecked({}); }}>Start another</Button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: 'var(--sp-3)',
  border: '1px solid var(--ink-300)',
  borderRadius: 'var(--r-md)',
  fontSize: 13,
};

const errorStyle: React.CSSProperties = {
  padding: 'var(--sp-3)',
  background: 'var(--err-bg)',
  color: 'var(--err-text)',
  borderRadius: 'var(--r-md)',
  fontSize: 12,
};

const infoNoteStyle: React.CSSProperties = {
  padding: 'var(--sp-3)',
  background: 'var(--info-bg)',
  color: 'var(--info-text)',
  borderRadius: 'var(--r-md)',
  fontSize: 12,
};

const successStyle: React.CSSProperties = {
  padding: 'var(--sp-4)',
  background: 'var(--ok-bg, #e8f5e9)',
  color: 'var(--ok-text, #1b5e20)',
  borderRadius: 'var(--r-md)',
  fontSize: 13,
};

function Field({ label, children }: { label: string; children: React.ReactNode }): JSX.Element {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-1)' }}>
      <span style={{ fontSize: 11, textTransform: 'uppercase', color: 'var(--ink-500)' }}>{label}</span>
      {children}
    </label>
  );
}
